import { Label } from '@/components/ui/label';
import { buildLocaleGroups } from '@/lib/blog/locale-options';
import type { BlogLocaleEntry } from '@/lib/blog/types';
import { cn } from '@/lib/utils';

interface LocaleSelectProps {
  id: string;
  entries: BlogLocaleEntry[];
  value: string;
  onChange: (locale: string) => void;
  className?: string;
}

/**
 * Every locale the cluster could have, grouped written / not written (Q2).
 *
 * A native `<select>` with `<optgroup>` rather than the Radix wrapper: the
 * whole roster in two groups is exactly what the element is for, the grouping is
 * announced by every screen reader without extra wiring, and the list never has
 * to be re-styled for RTL. The Radix version buys a custom check mark and costs
 * a portal.
 */
export function LocaleSelect({ id, entries, value, onChange, className }: LocaleSelectProps) {
  const groups = buildLocaleGroups(entries);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Label htmlFor={id}>Locale</Label>
      <select
        id={id}
        value={value}
        onChange={(event) => { onChange(event.target.value); }}
        className="h-10 rounded-2xl border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}
